import * as React from "react";

interface FormState {
  nominalDate: Date | null;
  startTime: Date | null;
  endTime: Date | null;
  center: number[];
}

interface OrderState extends FormState {
  timezone: string;
  zoom: number;
  loading: boolean;
}

interface DetailsAction extends FormState {
  type: "SET_DETAILS";
}

type OrderAction =
  | DetailsAction
  | { type: "SET_TIMEZONE"; timezone: string }
  | { type: "SET_DATE"; date: Date }
  | { type: "SET_CENTER"; center: number[] }
  | { type: "SET_ZOOM"; zoom: number }
  | { type: "SET_START_TIME"; time: Date }
  | { type: "SET_END_TIME"; time: Date };

const initialState: OrderState = {
  nominalDate: null,
  startTime: null,
  endTime: null,
  timezone: "",
  center: [0, 0],
  zoom: 12,
  loading: true,
};

const reducer = (state: OrderState, action: OrderAction) => {
  if (action.type === "SET_DETAILS") {
    return {
      ...state,
      ...action,
    };
  }

  if (action.type === "SET_TIMEZONE") {
    return {
      ...state,
      timezone: action.timezone,
    };
  }

  if (action.type === "SET_DATE") {
    return {
      ...state,
      nominalDate: action.date,
    };
  }

  if (action.type === "SET_CENTER") {
    return {
      ...state,
      center: action.center,
    };
  }

  if (action.type === "SET_ZOOM") {
    return {
      ...state,
      zoom: action.zoom,
    };
  }

  if (action.type === "SET_START_TIME") {
    return {
      ...state,
      startTime: action.time,
    };
  }

  if (action.type === "SET_END_TIME") {
    return {
      ...state,
      endTime: action.time,
    };
  }

  return state;
};

const useSurveyOrder = () => {
  const [
    { nominalDate, startTime, endTime, timezone, center, zoom, loading },
    dispatch,
  ] = React.useReducer(reducer, initialState);

  const setDetails = React.useCallback(
    (details: FormState) => dispatch({ type: "SET_DETAILS", ...details }),
    []
  );

  const setCenter = React.useCallback(
    (center: number[]) => dispatch({ type: "SET_CENTER", center }),
    []
  );

  const setZoom = React.useCallback(
    (zoom: number) => dispatch({ type: "SET_ZOOM", zoom }),
    []
  );

  const setTimezone = React.useCallback(
    (timezone: string) => dispatch({ type: "SET_TIMEZONE", timezone }),
    []
  );

  const setDate = React.useCallback(
    (date: Date) => dispatch({ type: "SET_DATE", date }),
    []
  );

  const setStartTime = React.useCallback(
    (time: Date) => dispatch({ type: "SET_START_TIME", time }),
    []
  );

  const setEndTime = React.useCallback(
    (time: Date) => dispatch({ type: "SET_END_TIME", time }),
    []
  );

  React.useEffect(() => {
    if (center) {
      let cancel = false;

      fetch(`/api/v1/timezone/${center[0]}/${center[1]}`)
        .then((response) => response.json())
        .then((zone) => {
          if (cancel === false) {
            dispatch({ type: "SET_TIMEZONE", timezone: zone.timezone });
          }
        });

      return () => {
        cancel = true;
      };
    }
  }, [center]);

  return {
    nominalDate,
    startTime,
    endTime,
    timezone,
    center,
    zoom,
    loading,
    setDetails,
    setCenter,
    setZoom,
    setTimezone,
    setDate,
    setStartTime,
    setEndTime,
  };
};

export default useSurveyOrder;
